import { useCallback, useEffect, useRef, useState } from 'react';

import type { PresenceActivity } from '@atlaskit/editor-common/collab';

import type { InitialDraft } from '@confluence/fabric-providers/entry-points/fetchDraft.ts';
import { getCollabEditProvider } from '@confluence/fabric-providers/entry-points/NativeCollabProvider.ts';
import { getDraft } from '@confluence/fabric-providers/entry-points/getDraft.ts';
import { useFetchUsersClient } from '@confluence/fabric-providers/entry-points/useFetchUsersClient.ts';
import { preloadEditorData } from '@confluence/load-edit-page/entry-points/preloadEditorData.ts';
import { useEditPageLoadingActions } from '@confluence/editor-loading-context/entry-points/EditPageLoadingContext.ts';
import { useNativeCollabPresenceState } from '@confluence/native-collab/entry-points/useNativeCollabPresenceState.ts';
import { useNativeCollabActions } from '@confluence/native-collab/entry-points/useNativeCollabState.ts';
import { fetchCollabPermissionToken } from '@confluence/native-collab/entry-points/CollabPermissionsToken.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { fg } from '@confluence/feature-gating';
import { getGlobalTransitionPreloader } from '@confluence/route-manager/entry-points/getGlobalTransitionPreloader.ts';
import { expValEquals } from '@confluence/feature-experiments';

export interface EditorDataLoaderProps {
	contentId: string;
	spaceKey: string | undefined;
	canEdit: boolean;
	isLoadingPermissionCheck: boolean;
}
interface EditorDataLoaderOutput {
	isPreloading: boolean;
	errors: any[];
}

export const useEditorDataLoader = ({
	isLoadingPermissionCheck,
	contentId,
	spaceKey,
	canEdit,
}: EditorDataLoaderProps): EditorDataLoaderOutput => {
	const { userId, cloudId, activationId } = useSessionData();
	const { nativeEditorComponentsPreloader } = useEditPageLoadingActions();
	const { setCollabEditProvider, setHasInitialDraft } = useNativeCollabActions();
	const { client: fetchUsersClient } = useFetchUsersClient();

	// presenceId is pulled from the native collab state to ensure that we have a consistent presenceId for a given contentId
	// across the collab providers that drive editor as well as presence components (telepointer and avatar colors).
	const [{ presenceId }] = useNativeCollabPresenceState();
	const presenceIdRef = useRef(presenceId);
	presenceIdRef.current = presenceId;

	const [isPreloading, setIsPreloading] = useState(true);
	const [errors, setErrors] = useState<any[]>([]);
	const latestPreloadingContentIdRef = useRef<string | null>(null);

	const getNCSCollabProvider = useCallback(
		(draft: InitialDraft | null, contentType: string, activity: PresenceActivity) => {
			try {
				const ncsProvider = getCollabEditProvider({
					contentId,
					cloudId,
					activationId,
					contentType,
					permissionTokenRefresh: fetchCollabPermissionToken(contentId, undefined),
					initialDraft: draft,
					isLivePage: true,
					presenceId: presenceIdRef.current,
					presenceActivity: activity,
					userId,
					fetchUsersClient,
				});
				setCollabEditProvider(ncsProvider);
				// @ts-ignore
				const hasInitialDraft = Boolean(ncsProvider?.initialDraft);
				setHasInitialDraft(hasInitialDraft);
			} catch (error) {
				setErrors((prevErrors) => [...prevErrors, error]);
			}
		},
		[
			activationId,
			cloudId,
			contentId,
			presenceIdRef,
			fetchUsersClient,
			userId,
			setCollabEditProvider,
			setHasInitialDraft,
		],
	);

	const transitionPreloaderCurrentCallbackIDs =
		getGlobalTransitionPreloader().getCurrentRouteCallbackIds();
	// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
	const isSeamlessTransition = fg('confluence_frontend_transition_preloading_system')
		? transitionPreloaderCurrentCallbackIDs.includes('live_doc_preloading_transition_callback_id')
		: false;

	// Preload should be called once per contentId
	useEffect(() => {
		// Seamless transitioning ensures the live doc has already been preloaded in a similar fashion to how this hook works, so we can safely skip this hook redundantly preloading.
		// Skip preloading if permission check is still loading
		if (isSeamlessTransition || isLoadingPermissionCheck) {
			return;
		}

		const preload = async () => {
			try {
				const contentType = 'page';
				const activity = canEdit ? 'editor' : 'viewer';
				const getDraftPromise: Promise<InitialDraft | null> = getDraft(contentId);
				const results = await Promise.all([
					expValEquals('confluence_live_pages_unified_query_v2', 'isEnabled', true)
						? Promise.resolve() // does nothing, just keeps order of destructured results
						: preloadEditorData({
								spaceKey,
								contentId,
								useCache: true, // use cache for preload queries
								cloudId,
								isBlog: false,
								isLivePage: true,
							}),
					nativeEditorComponentsPreloader(),
					getDraftPromise,
				]);
				const [, , draftResponse] = results;

				/**
				 * This if condition fixes issue where transitioning between different live docs too quickly cause NCS collab provider not connected
				 * The issue happens when user has already transitioned away from the page when the promise.all above finished
				 * To avoid the issue, only get a new NCS collab provider and finish preloading if the latest preloading content id matches the preloaded content Id
				 */
				if (latestPreloadingContentIdRef.current === contentId) {
					getNCSCollabProvider(draftResponse, contentType, activity);
					setIsPreloading(false);
					latestPreloadingContentIdRef.current = null;
				}
			} catch (error) {
				setErrors((prevErrors) => [...prevErrors, error]);
			}
		};
		latestPreloadingContentIdRef.current = contentId;
		preload().catch((error) => {
			setErrors((prevErrors) => [...prevErrors, error]);
		});
		return () => {
			setIsPreloading(true);
		};
		// The dependency array should not change per page edit
		// This prevents multiple preloads for the same contentId, which can cause NCS connection issues
	}, [
		canEdit,
		cloudId,
		contentId,
		spaceKey,
		getNCSCollabProvider,
		nativeEditorComponentsPreloader,
		userId,
		latestPreloadingContentIdRef,
		isSeamlessTransition,
		isLoadingPermissionCheck,
	]);

	return { isPreloading: isSeamlessTransition ? false : isPreloading, errors };
};
